<template>
  <div>
    <div class="d-flex align-items-center flex-wrap gap-3 mb-4">
      <h1 class="cg-page-title mb-0">{{ rollup.meta.title }}</h1>
      <YearPicker v-model:year="year" :options="YEARS" />
    </div>

    <div
      v-if="!isCurrentYear"
      class="d-flex align-items-center gap-3 mb-4 p-3 border rounded bg-body-secondary"
    >
      <ToneBadge :tone="yearOption.tone" :label="yearOption.state" />
      <span class="flex-grow-1 small">{{
        yearOption.banner || yearEmptyBody
      }}</span>
      <button
        type="button"
        class="btn btn-sm btn-outline-secondary"
        @click="year = DEMO_YEAR"
      >
        Back to {{ DEMO_YEAR }}
      </button>
    </div>

    <template v-if="isCurrentYear">
      <SegmentedControl
        v-model="view"
        :options="viewOptions"
        fit
        class="mb-4"
      />

      <template v-if="view === 'position'">
        <AnnualBalanceCard
          :rollup
          @open-incoming="view = 'incoming'"
          @open-outgoing="view = 'outgoing'"
        />
        <PlannedBySolderaCard :rows="rollup.plan" />
      </template>

      <template v-else-if="view === 'incoming'">
        <OpenItemsList :items="rollup.incoming" @action="runAction" />
        <PpaSupplyList
          :ppas="rollup.ppaCards"
          :domains="store.domains"
          @open="openPpa"
        />
        <h2 class="cg-section-title mb-3 mt-4">Other sources</h2>
        <StaticSupplyCards />
      </template>

      <template v-else-if="view === 'outgoing'">
        <OpenItemsList :items="rollup.outgoing" @action="runAction" />
        <div
          class="d-flex justify-content-between align-items-end flex-wrap gap-3 mb-3"
        >
          <h2 class="cg-section-title">Claim position</h2>
          <RebalanceNote v-if="rollup.rebalance" :note="rollup.rebalance" />
        </div>
        <ClaimPositionCard
          v-for="row in rollup.domainRows"
          :key="row.key"
          :row
          @open="openDomain"
        />
        <BufferCard :buffer="rollup.balance.buffer" />
      </template>

      <EvidenceTab v-else :group />
    </template>

    <div v-else class="card">
      <div class="card-body">
        <h2 class="cg-section-title mb-1">{{ yearEmptyTitle }}</h2>
        <p class="mb-0 text-body-secondary small">{{ yearEmptyBody }}</p>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import SegmentedControl, {
  type SegmentedControlOption,
} from "@/components/common/SegmentedControl.vue";
import { useQueryManager } from "@/composables/useQueryManager";
import { computed, watch } from "vue";
import { useRoute, useRouter } from "vue-router";
import AnnualBalanceCard from "./components/AnnualBalanceCard.vue";
import BufferCard from "./components/BufferCard.vue";
import ClaimPositionCard from "./components/ClaimPositionCard.vue";
import EvidenceTab from "./components/EvidenceTab.vue";
import OpenItemsList from "./components/OpenItemsList.vue";
import PlannedBySolderaCard from "./components/PlannedBySolderaCard.vue";
import PpaSupplyList from "./components/PpaSupplyList.vue";
import RebalanceNote from "./components/RebalanceNote.vue";
import StaticSupplyCards from "./components/StaticSupplyCards.vue";
import ToneBadge from "./components/ToneBadge.vue";
import YearPicker from "./components/YearPicker.vue";
import {
  DEMO_YEAR,
  isGroupKey,
  YEARS,
} from "./fixture/complianceGroupsFixture";
import type {
  DomainKey,
  GroupKey,
  PpaKey,
} from "./fixture/complianceGroupsTypes";
import type { OpenItemAction } from "./fixture/derive";
import { useComplianceDemoStore } from "./useComplianceDemoStore";

type GroupView = "position" | "incoming" | "outgoing" | "evidence";
const VIEWS: GroupView[] = ["position", "incoming", "outgoing", "evidence"];

const route = useRoute();
const router = useRouter();
const store = useComplianceDemoStore();
const { getQueryValue, parsers } = useQueryManager();

const group = computed<GroupKey>(() =>
  isGroupKey(route.params.group) ? route.params.group : "eecs"
);
// Unknown group segments are not a record: send them to the default group instead of
// silently rendering it under the wrong URL.
watch(
  () => route.params.group,
  (value) => {
    if (value !== undefined && !isGroupKey(value)) {
      router.replace({ name: "ComplianceGroup", params: { group: "eecs" } });
    }
  },
  { immediate: true }
);
const rollup = computed(() => store.rollups[group.value]);

const viewQuery = getQueryValue("view", parsers.enum(VIEWS));
const view = computed<GroupView>({
  get: () => viewQuery.value ?? "position",
  set: (value) => (viewQuery.value = value),
});
const yearQuery = getQueryValue("year", parsers.number);
const year = computed<number>({
  get: () =>
    YEARS.some((option) => option.year === yearQuery.value)
      ? (yearQuery.value as number)
      : DEMO_YEAR,
  set: (value) => (yearQuery.value = value === DEMO_YEAR ? undefined : value),
});
const isCurrentYear = computed(() => year.value === DEMO_YEAR);
const yearOption = computed(
  () => YEARS.find((y) => y.year === year.value) ?? YEARS[1]
);
const yearEmptyTitle = computed(() =>
  year.value < DEMO_YEAR
    ? `Nothing on file for ${year.value}`
    : `${year.value} planning workspace`
);
const yearEmptyBody = computed(() =>
  year.value < DEMO_YEAR
    ? `No consumption, agreements or certificates were recorded for ${year.value}.`
    : `Forward-year contracting is not part of this demo. Switch back to ${DEMO_YEAR} to see live balance, supply, and allocation.`
);

const viewOptions = computed<SegmentedControlOption<GroupView>[]>(() => [
  { value: "position", label: `Position · ${DEMO_YEAR}` },
  {
    value: "incoming",
    label: "Incoming · supply",
    count: rollup.value.incoming.length,
  },
  {
    value: "outgoing",
    label: "Outgoing · allocation",
    count: rollup.value.outgoing.length,
  },
  {
    value: "evidence",
    label: "Evidence · statements",
    count: store.statementsFor(group.value).length,
    countMuted: true,
  },
]);

const openPpa = (key: PpaKey) =>
  router.push({
    name: "ComplianceGroupPpa",
    params: { group: group.value, ppa: key },
  });
const openDomain = (key: DomainKey) =>
  router.push({
    name: "ComplianceGroupDomain",
    params: { group: group.value, domain: key },
  });
const runAction = (action: OpenItemAction) => {
  if (action.type === "ppa") return openPpa(action.key);
  if (action.type === "domain") return openDomain(action.key);
  if (action.type === "resolve")
    return router.push({
      name: "ComplianceGroupSourceRequest",
      params: { group: "eecs" },
    });
  return router.push({
    name: "ComplianceGroupRequestSubmitted",
    params: { group: "eecs" },
  });
};
</script>
